import { useCallback, useState } from 'react';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { casesExportFilename, getCasesExportXlsxBlob, triggerXlsxDownload } from '@/lib/cases';

interface CasosExportButtonProps {
  /** Filtros server-side ativos (espelham os enviados ao `listCases`). */
  cpf: string;
  contrato: string;
}

/**
 * Botão de export consolidado dos casos em planilha (E44.F1 / ADR-0055). // ux-neutrality:allow doc-comment
 *
 * Encapsula estado de carregamento/erro e o download do XLSX, mantendo a // ux-neutrality:allow doc-comment
 * página de casos enxuta. Exporta todos os casos do escritório que batem os // ux-neutrality:allow doc-comment
 * filtros de CPF/contrato, sem teto de paginação. // ux-neutrality:allow doc-comment
 */
export function CasosExportButton({ cpf, contrato }: CasosExportButtonProps) {
  const apiClient = useApiClient();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = useCallback(() => {
    setExporting(true);
    setError(null);
    getCasesExportXlsxBlob(apiClient, { cpf, contrato })
      .then((blob) => {
        triggerXlsxDownload(blob, casesExportFilename(new Date()));
      })
      .catch(() => {
        setError(ptBR.casos.export.error);
      })
      .finally(() => {
        setExporting(false);
      });
  }, [apiClient, cpf, contrato]);

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="secondary"
        data-testid="exportar-casos-btn"
        className="h-[38px]"
        onClick={handleExport}
        disabled={exporting}
      >
        {exporting ? ptBR.casos.export.exporting : ptBR.casos.export.button}
      </Button>
      {error !== null ? (
        <p role="alert" data-testid="casos-export-error" className="text-body-sm text-danger-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
